import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Coins, Play, Check, X, TreeStructure } from '@phosphor-icons/react';

const HABITATS = [
    { id: 'reef', name: 'Coral Reef', emoji: '🪸', color: '#38bdf8' },
    { id: 'outback', name: 'Outback', emoji: '🏜️', color: '#f59e0b' },
    { id: 'rainforest', name: 'Rainforest', emoji: '🌿', color: '#22c55e' },
    { id: 'wetlands', name: 'Wetlands', emoji: '🌊', color: '#a855f7' },
];

const ANIMALS_DATA = [
    { name: 'Clownfish', emoji: '🐠', habitat: 'reef' },
    { name: 'Sea Turtle', emoji: '🐢', habitat: 'reef' },
    { name: 'Kangaroo', emoji: '🦘', habitat: 'outback' },
    { name: 'Echidna', emoji: '🦔', habitat: 'outback' },
    { name: 'Cassowary', emoji: '🐦‍⬛', habitat: 'rainforest' },
    { name: 'Tree Frog', emoji: '🐸', habitat: 'rainforest' },
    { name: 'Platypus', emoji: '🦆', habitat: 'wetlands' },
    { name: 'Pelican', emoji: '🐦', habitat: 'wetlands' },
    { name: 'Dugong', emoji: '🐋', habitat: 'reef' },
    { name: 'Dingo', emoji: '🐕', habitat: 'outback' },
    { name: 'Koala', emoji: '🐨', habitat: 'rainforest' },
    { name: 'Crocodile', emoji: '🐊', habitat: 'wetlands' },
];

function shuffle(arr) {
    const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a;
}

export default function HabitatBuilderGame() {
    const { api } = useAuth();
    const [started, setStarted] = useState(false);
    const [animals, setAnimals] = useState([]);
    const [placements, setPlacements] = useState({});
    const [selectedAnimal, setSelectedAnimal] = useState(null);
    const [checked, setChecked] = useState(false);
    const [score, setScore] = useState(0);
    const [result, setResult] = useState(null);

    const startGame = () => {
        setAnimals(shuffle(ANIMALS_DATA));
        setPlacements({}); setSelectedAnimal(null); setChecked(false); setScore(0); setResult(null); setStarted(true);
    };

    const handlePlaceInHabitat = (habitatId) => {
        if (!selectedAnimal || checked) return;
        setPlacements(prev => ({ ...prev, [selectedAnimal.name]: habitatId }));
        setSelectedAnimal(null);
    };

    const handleCheck = async () => {
        let correct = 0;
        animals.forEach(a => { if (placements[a.name] === a.habitat) correct++; });
        setScore(correct);
        setChecked(true);
        try {
            const res = await api().post('/games/complete', {
                game_id: 'habitat_builder', score: Math.round((correct / animals.length) * 100), difficulty: 'medium'
            });
            setResult(res.data);
        } catch (err) { console.error('Failed to submit'); }
    };

    const allPlaced = Object.keys(placements).length === animals.length;

    if (!started) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
                <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center h-20">
                            <Link to="/games" className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-full bg-[#a855f7] border-4 border-[#1e293b] flex items-center justify-center">
                                    <TreeStructure weight="fill" className="text-white" size={20} />
                                </div>
                                <span className="font-fredoka text-xl font-bold text-[#1e293b]">Habitat Builder</span>
                            </Link>
                            <Link to="/games" className="brutalist-btn bg-white text-[#1e293b] text-sm flex items-center gap-1.5">
                                <ArrowLeft weight="bold" size={16} /> Games
                            </Link>
                        </div>
                    </div>
                </nav>
                <div className="max-w-2xl mx-auto px-4 pt-28 pb-24 text-center">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="text-6xl mb-4">🏠</div>
                        <h1 className="font-fredoka text-3xl font-bold text-[#1e293b] mb-2">Habitat Builder</h1>
                        <p className="text-[#475569] mb-8">Place 12 Australian animals into their correct habitat: Coral Reef, Outback, Rainforest, or Wetlands.</p>
                        <motion.button onClick={startGame} whileHover={{ y: -4, x: -4 }} whileTap={{ y: 2, x: 2 }}
                            className="brutalist-btn bg-[#a855f7] text-white text-lg flex items-center gap-2 mx-auto" data-testid="start-game">
                            <Play weight="bold" size={22} /> Start
                        </motion.button>
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)]">
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b] px-4 py-3">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <Link to="/games" className="text-[#475569] font-semibold flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Games</Link>
                    <span className="text-sm font-semibold">{Object.keys(placements).length}/{animals.length} placed</span>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 pt-16 pb-8">
                {/* Animals to place */}
                {!checked && (
                    <div className="mb-6 p-4 bg-white border-4 border-[#1e293b] rounded-2xl">
                        <p className="font-fredoka font-bold text-[#1e293b] mb-3">
                            {selectedAnimal ? `Place ${selectedAnimal.emoji} ${selectedAnimal.name} in a habitat below` : 'Select an animal to place:'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {animals.filter(a => !placements[a.name]).map(a => (
                                <motion.button key={a.name} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                                    onClick={() => setSelectedAnimal(a)}
                                    className={`px-3 py-2 rounded-xl border-2 font-semibold text-sm flex items-center gap-1.5 transition-colors ${selectedAnimal?.name === a.name ? 'border-[#22c55e] bg-[#22c55e]/10' : 'border-[#1e293b] bg-white hover:bg-[#f8fafc]'}`}
                                    data-testid={`animal-${a.name}`}>
                                    <span className="text-lg">{a.emoji}</span> {a.name}
                                </motion.button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Habitats */}
                <div className="grid sm:grid-cols-2 gap-4">
                    {HABITATS.map(h => {
                        const placed = animals.filter(a => placements[a.name] === h.id);
                        return (
                            <div key={h.id}
                                onClick={() => handlePlaceInHabitat(h.id)}
                                className={`brutalist-card p-4 min-h-[140px] transition-shadow ${selectedAnimal && !checked ? 'cursor-pointer hover:shadow-[6px_6px_0px_0px_#1e293b]' : ''}`}
                                style={{ borderColor: selectedAnimal ? h.color : undefined }}
                                data-testid={`habitat-${h.id}`}>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-2xl">{h.emoji}</span>
                                    <h3 className="font-fredoka font-bold text-[#1e293b]">{h.name}</h3>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {placed.map(a => {
                                        const isCorrect = checked && a.habitat === h.id;
                                        const isWrong = checked && a.habitat !== h.id;
                                        return (
                                            <span key={a.name} className={`px-2 py-1 rounded-lg text-sm font-semibold flex items-center gap-1 ${isCorrect ? 'bg-[#22c55e]/20 text-[#22c55e]' : isWrong ? 'bg-[#ef4444]/20 text-[#ef4444]' : 'bg-[#f8fafc]'}`}>
                                                {a.emoji} {a.name} {isCorrect && <Check size={14} />} {isWrong && <X size={14} />}
                                            </span>
                                        );
                                    })}
                                    {placed.length === 0 && <span className="text-sm text-[#94a3b8]">Drop animals here</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Actions */}
                <div className="mt-6 text-center">
                    {!checked ? (
                        <motion.button onClick={handleCheck} disabled={!allPlaced} whileHover={{ y: -2, x: -2 }}
                            className="brutalist-btn bg-[#22c55e] text-white disabled:opacity-50 disabled:cursor-not-allowed" data-testid="check-answers">
                            Check Answers ({Object.keys(placements).length}/{animals.length})
                        </motion.button>
                    ) : (
                        <div className="brutalist-card p-6 bg-white inline-block" data-testid="game-complete">
                            <p className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">{score}/{animals.length} Correct!</p>
                            {result && <p className="text-[#f59e0b] font-bold mb-4">+{result.tokens_earned} Bush Tokens</p>}
                            <div className="flex gap-3 justify-center">
                                <button onClick={startGame} className="brutalist-btn bg-[#a855f7] text-white"><Play weight="bold" size={18} /> Again</button>
                                <Link to="/games" className="brutalist-btn bg-white text-[#1e293b]">Games Hub</Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
